import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Lock, Zap, Crown, Play, Gift } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function UnlockModal({ trip, onClose, onUnlocked }) {
  const [loading, setLoading] = useState(null);
  const [adRunning, setAdRunning] = useState(false);
  const { user } = useAuth();

  const pay2 = async () => {
    setLoading("pay");
    try {
      const r = await api.post("/contacts/unlock/checkout", { trip_id: trip.trip_id, origin_url: window.location.origin });
      if (r.data.already_unlocked) { onUnlocked(r.data.contact_id); return; }
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); setLoading(null); }
  };

  const useSubscription = async () => {
    try {
      const r = await api.post("/contacts/unlock/subscription", { trip_id: trip.trip_id });
      toast.success("Contact débloqué via abonnement");
      onUnlocked(r.data.contact_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Aucun abonnement actif"); }
  };

  const useCredit = async () => {
    try {
      const r = await api.post("/contacts/unlock/credit", { trip_id: trip.trip_id });
      toast.success("Contact débloqué (crédit parrainage)");
      onUnlocked(r.data.contact_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Aucun crédit gratuit"); }
  };

  const startAd = () => {
    setAdRunning(true);
    setTimeout(async () => {
      try {
        const r = await api.post("/contacts/unlock/ad", { trip_id: trip.trip_id });
        toast.success("Merci ! Contact débloqué.");
        onUnlocked(r.data.contact_id);
      } catch { toast.error("Erreur"); }
      setAdRunning(false);
    }, 3000); // 3s demo (real 60s)
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="glass-strong rounded-2xl w-full max-w-lg p-6 relative">
        <button data-testid="unlock-close" onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-white"><X size={18}/></button>
        <div className="flex items-center gap-2 text-[#FF6B6B] mb-1"><Lock size={16}/> <span className="text-xs uppercase tracking-wider">Déblocage contact</span></div>
        <h3 className="font-display text-xl font-bold">{trip.origin.label} → {trip.destination.label}</h3>
        <p className="text-xs text-slate-400 mb-5">Choisissez votre méthode :</p>

        <div className="space-y-3">
          <button data-testid="unlock-pay-2" onClick={pay2} disabled={loading} className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-gradient-to-br from-[#FF6B6B] to-[#FA5252] text-white">
            <span className="flex items-center gap-2"><Zap size={16}/> Payer <b>2€</b> une fois</span>
            <span className="text-xs bg-white/20 rounded-full px-2 py-0.5">Rapide</span>
          </button>
          {user?.free_unlocks > 0 && (
            <button data-testid="unlock-credit" onClick={useCredit} className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/40 text-[#F59E0B]">
              <span className="flex items-center gap-2"><Gift size={16}/> Utiliser un crédit gratuit</span>
              <span className="text-xs bg-[#F59E0B]/20 rounded-full px-2 py-0.5">{user.free_unlocks} restant(s)</span>
            </button>
          )}
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="unlock-medium" onClick={useSubscription} className="btn-ghost-glass px-3 py-3 rounded-xl text-sm text-left">
              <div className="flex items-center gap-2 mb-1"><Crown size={14} className="text-[#F59E0B]"/> Medium/Premium</div>
              <div className="text-xs text-slate-400">Utiliser mon abonnement</div>
            </button>
            <button data-testid="unlock-premium" onClick={()=>window.location.href="/settings"} className="btn-ghost-glass px-3 py-3 rounded-xl text-sm text-left">
              <div className="flex items-center gap-2 mb-1"><Crown size={14} className="text-[#F59E0B]"/> S'abonner</div>
              <div className="text-xs text-slate-400">20€/30€ par mois</div>
            </button>
          </div>
          <button data-testid="unlock-ad" onClick={startAd} disabled={adRunning} className="w-full flex items-center justify-between btn-ghost-glass px-4 py-3 rounded-xl text-sm">
            <span className="flex items-center gap-2"><Play size={14}/> {adRunning ? "Pub en cours…" : "Regarder une pub (gratuit)"}</span>
            <span className="text-[11px] text-slate-400">≈ 1 min</span>
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-4">Paiement sécurisé Stripe. Test : carte 4242 4242 4242 4242.</p>
      </div>
    </div>
  );
}
